/**
 * DawPrescriptions.tsx
 *
 * DAW (ScriptSure) side of the patient Prescriptions tab. Opens a prescribing
 * session via POST /patients/{id}/prescriptions/daw/session. The automatic
 * (bodyless) open decides what the tab shows: its actions, the
 * practice/prescriber picker, or an error. It is a cached react-query query
 * keyed by patient, so bouncing between chart tabs within the staleTime
 * reuses its answer instead of re-hitting the vendor. When the user's
 * ScriptSure scope is ambiguous the server answers `needsSelection` and the
 * picker submits an explicit mutation whose result seeds the same query cache.
 *
 * The tab opens four ScriptSure screens: the prescribing chart (Prescribe),
 * and, grouped apart from Prescribe and Refresh, this patient's Messages,
 * Medication history (downloaded from pharmacies and insurers) and
 * Prescription history (what was prescribed through ScriptSure). Every click
 * asks the server again through that same mutation, and the window opens at
 * the URL of that answer rather than the cached one. The server sends any
 * consent withdrawal still owed to ScriptSure before it hands out a window,
 * and refuses the window when one cannot be sent (plan R14, KTD4). A
 * withdrawal left owed elsewhere (saved in another browser, or by a user who
 * cannot sign in to ScriptSure) would otherwise wait out the cached answer,
 * letting ScriptSure keep a yes the patient has since withdrawn.
 *
 * Medication history reads the patient's consent first. Without it the tab
 * opens nothing, says so, and links to Demographics where it is added (plan
 * R11): ScriptSure would only show an empty screen.
 *
 * Each window is the shared ScriptSureWindow. The patient's prescriptions,
 * with their status history and pharmacy messages, are listed on the tab
 * itself (DawPrescriptionList); Refresh and ScriptSure's '200' signal reload
 * that list. Rendered by PrescriptionsTab when the context answer is
 * `provider: 'daw'`.
 */
import { useEffect, useState } from 'react';
import { useIsFetching, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ClipboardList,
  History,
  Loader2,
  MessageSquare,
  Pill,
  RefreshCw,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ScopePicker, type ScopeChoice } from '@/components/scriptsure/ScopePicker';
import {
  ScriptSureErrorNotice,
  errorViewFor,
  notConfiguredView,
  type ScriptSureErrorView,
} from '@/components/scriptsure/ScriptSureError';
import { ScriptSureWindow, scriptSureWindowLabel } from '@/components/scriptsure/ScriptSureWindow';
import { DawPrescriptionList } from './DawPrescriptionList';
import {
  createDawSession,
  dawPrescriptionListKey,
  getDawErrorCode,
  getMedicationHistoryConsent,
  type DawPatientWindow,
  type DawPrescriptionsContext,
  type DawSessionBody,
  type DawSessionReady,
  type DawSessionResult,
  type DawSessionSelection,
} from '@/services/prescriptions.service';

/**
 * How long a remount reuses the automatic open's answer instead of re-hitting
 * the vendor. That answer only decides what the tab shows; each window click
 * asks the server again, because the server sends any consent withdrawal
 * still owed to ScriptSure before it hands out a window (plan R14, KTD4).
 */
const SESSION_STALE_MS = 10 * 60_000;

const CONSENT_ABSENT_TEXT =
  "This patient hasn't agreed to let the practice download their medication history.";

/** The consent read failed, so whether the patient agreed is not known. */
const CONSENT_UNREADABLE_TEXT =
  "We couldn't check whether this patient agreed to let the practice download their medication history. Try again, and contact support if it keeps happening.";

/** The look-up screens, grouped apart from Prescribe and Refresh. */
const LOOKUP_WINDOWS: { kind: DawPatientWindow; icon: LucideIcon }[] = [
  { kind: 'patient_messages', icon: MessageSquare },
  { kind: 'medication_history', icon: History },
  { kind: 'prescribed_history', icon: ClipboardList },
];

type DawView =
  | { kind: 'pending' }
  | { kind: 'select'; selection: DawSessionSelection }
  | { kind: 'ready'; session: DawSessionReady }
  | { kind: 'error'; error: ScriptSureErrorView };

/** The window on screen, tagged with the patient it was opened for. */
interface OpenPatientWindow {
  patientId: number;
  kind: DawPatientWindow;
  url: string;
}

interface DawPrescriptionsProps {
  patientId: number;
  context: DawPrescriptionsContext;
}

export function DawPrescriptions({ patientId, context }: DawPrescriptionsProps) {
  const queryClient = useQueryClient();
  const [openWindow, setOpenWindow] = useState<OpenPatientWindow | null>(null);
  // The window a needsSelection answer interrupted. The picker's Continue asks
  // for it again with both ids; null when the picker came from the automatic
  // open, whose Continue only settles the tab's scope.
  const [awaitingScope, setAwaitingScope] = useState<DawPatientWindow | null>(null);
  // The picker's last submitted choice, shown again if that submit failed and
  // the picker comes back.
  const [lastChoice, setLastChoice] = useState<ScopeChoice | null>(null);

  // While the cached answer is a needsSelection round-trip the automatic
  // (bodyless) open must stay off — re-firing it would clobber the picker.
  // Reading the cache directly is safe here: the useQuery subscription below
  // re-renders this component on every cache change, so the gate stays fresh.
  const cachedSession = queryClient.getQueryData<DawSessionResult>(['daw-session', patientId]);
  const sessionQuery = useQuery({
    queryKey: ['daw-session', patientId],
    queryFn: () => createDawSession(patientId),
    enabled: context.configured && cachedSession?.needsSelection !== true,
    staleTime: SESSION_STALE_MS,
    retry: false,
    // Never on its own (main.tsx turns refetch-on-focus on for every query).
    // Every ScriptSure call must come from a click (plan R4), and this open
    // re-scopes the user's one ScriptSure session, which would move a window
    // already open mid-signing onto whatever scope it lands on (KTD18).
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const mutation = useMutation({
    // Carries every explicit open: the picker's Continue and each window
    // click. The patient the submit was fired for travels with the variables
    // so a slow response landing after a chart switch seeds THAT patient's
    // cache entry — never the currently displayed one.
    mutationFn: ({ forPatientId, body }: { forPatientId: number; body: DawSessionBody }) =>
      createDawSession(forPatientId, body),
    onSuccess: (result, vars) => {
      queryClient.setQueryData<DawSessionResult>(['daw-session', vars.forPatientId], result);
    },
  });

  // Medication history's consent read, through the query key the
  // demographics editor keeps current. A mutation rather than a query so it
  // runs only on the click, and so the patient-switch reset below detaches an
  // answer still in flight, exactly as it does for the window opens.
  const consentCheck = useMutation({
    mutationFn: (forPatientId: number) =>
      queryClient.fetchQuery({
        queryKey: ['medication-history-consent', forPatientId],
        queryFn: () => getMedicationHistoryConsent(forPatientId),
        staleTime: 0,
      }),
  });

  // A patient switch drops the open window, the window waiting on the picker,
  // the picker's last choice, and the submit and consent state, settled or
  // still in flight (see requestWindow). The picker's draft goes with it (it is
  // keyed by patient below), and the session resets via the query key.
  const resetMutation = mutation.reset;
  const resetConsentCheck = consentCheck.reset;
  useEffect(() => {
    setOpenWindow(null);
    setAwaitingScope(null);
    setLastChoice(null);
    resetMutation();
    resetConsentCheck();
  }, [patientId, resetMutation, resetConsentCheck]);

  const sessionData = sessionQuery.data;

  // Submit state only overlays the view when it belongs to this patient.
  const mutationForThisPatient = mutation.variables?.forPatientId === patientId;

  let view: DawView;
  if (!context.configured) {
    // Known ahead of time from the context payload — don't bother the API,
    // and retrying can't fix it.
    view = { kind: 'error', error: notConfiguredView() };
  } else if (mutationForThisPatient && mutation.isPending) {
    view = { kind: 'pending' };
  } else if (mutationForThisPatient && mutation.isError) {
    // "Try again" drops the failed submit and shows what is still cached: the
    // picker after a failed Continue, the actions after a failed window click.
    // A window click refused for its practice or prescriber is the exception:
    // the cached ready answer holds that same scope, so every retry would be
    // refused the same way. It is dropped with the choice, and the tab asks
    // the server afresh, which picks a scope or offers the picker. (A refused
    // picker Continue keeps its cached picker: it holds no scope to resend.)
    const refusedScope = getDawErrorCode(mutation.error) === 'DAW_BAD_SCOPE'
      && sessionQuery.data?.needsSelection === false;
    view = {
      kind: 'error',
      error: errorViewFor(mutation.error, () => {
        resetMutation();
        if (refusedScope) {
          setLastChoice(null);
          void queryClient.resetQueries({ queryKey: ['daw-session', patientId] });
        }
      }),
    };
  } else if (sessionQuery.isError) {
    view = sessionQuery.isFetching
      ? { kind: 'pending' }
      : { kind: 'error', error: errorViewFor(sessionQuery.error, () => void sessionQuery.refetch()) };
  } else if (!sessionData) {
    view = { kind: 'pending' };
  } else if (sessionData.needsSelection) {
    view = { kind: 'select', selection: sessionData };
  } else {
    view = { kind: 'ready', session: sessionData };
  }

  const showList = context.configured && sessionData !== undefined && !sessionData.needsSelection;

  // The list runs its own query (DawPrescriptionList); Refresh only needs to
  // know whether it is already loading.
  const listKey = dawPrescriptionListKey(patientId);
  const listLoading = useIsFetching({ queryKey: listKey }) > 0;

  // A consent answer only counts for the patient it was read for.
  const consentForThisPatient = consentCheck.variables === patientId;
  const consentAbsent =
    consentForThisPatient && consentCheck.isSuccess && !consentCheck.data.consented;
  const consentUnreadable = consentForThisPatient && consentCheck.isError;

  function requestWindow(kind: DawPatientWindow, scope: ScopeChoice) {
    // The chart goes without `window`, so its body is the one the server
    // always took for a prescribing open.
    const body: DawSessionBody = kind === 'chart' ? { ...scope } : { ...scope, window: kind };
    // The window opens from this call's own callback, not the mutation-level
    // onSuccess: the patient-switch reset detaches this call, so an answer
    // that lands after a switch does not run it.
    mutation.mutate(
      { forPatientId: patientId, body },
      {
        onSuccess: (result) => {
          if (result.needsSelection) {
            setAwaitingScope(kind);
            return;
          }
          setAwaitingScope(null);
          setOpenWindow({ patientId, kind, url: result.iframeUrl });
        },
      },
    );
  }

  /** The scope of the answer on screen, which each window click sends back. */
  function readyScope(): ScopeChoice | null {
    if (view.kind !== 'ready') return null;
    // The ready answer's own practice and prescriber keep the user's earlier
    // choice, so a multi-practice user is not sent back to the picker; the
    // server re-checks both.
    return { practiceId: view.session.practiceId, prescriberId: view.session.prescriberId };
  }

  function handleOpen(kind: DawPatientWindow) {
    const scope = readyScope();
    if (!scope) return;
    // A later click replaces an earlier consent answer, or one still coming.
    resetConsentCheck();
    requestWindow(kind, scope);
  }

  function handleMedicationHistory() {
    const scope = readyScope();
    if (!scope) return;
    consentCheck.mutate(patientId, {
      onSuccess: (consent) => {
        if (consent.consented) requestWindow('medication_history', scope);
      },
    });
  }

  function handleContinue(choice: ScopeChoice) {
    setLastChoice(choice);
    if (awaitingScope) {
      requestWindow(awaitingScope, choice);
      return;
    }
    mutation.mutate({ forPatientId: patientId, body: choice });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">Prescriptions</h2>
        {view.kind === 'ready' && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => handleOpen('chart')}>
              <Pill className="mr-1 h-3.5 w-3.5" />
              {scriptSureWindowLabel('chart')}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void queryClient.refetchQueries({ queryKey: listKey })}
              disabled={listLoading}
            >
              <RefreshCw className="mr-1 h-3.5 w-3.5" />
              Refresh
            </Button>
          </div>
        )}
      </div>

      {view.kind === 'ready' && (
        <div className="flex flex-wrap items-center gap-2">
          {LOOKUP_WINDOWS.map(({ kind, icon: Icon }) => {
            const checking = kind === 'medication_history' && consentCheck.isPending;
            return (
              <Button
                key={kind}
                size="sm"
                variant="secondary"
                onClick={kind === 'medication_history' ? handleMedicationHistory : () => handleOpen(kind)}
                disabled={checking}
              >
                {checking ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Icon className="mr-1 h-3.5 w-3.5" />
                )}
                {scriptSureWindowLabel(kind)}
              </Button>
            );
          })}
        </div>
      )}

      {view.kind === 'ready' && consentAbsent && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p>{CONSENT_ABSENT_TEXT}</p>
          <a
            href={`/patients/${patientId}?section=demographics`}
            className="mt-1 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
          >
            Add their consent in Demographics
          </a>
        </div>
      )}

      {view.kind === 'ready' && consentUnreadable && (
        <ScriptSureErrorNotice
          view={{ code: null, text: CONSENT_UNREADABLE_TEXT, missing: [], retry: handleMedicationHistory }}
        />
      )}

      {view.kind === 'pending' && (
        <div className="flex items-center justify-center gap-2 p-6 text-sm text-slate-700">
          <Loader2 className="h-5 w-5 animate-spin" />
          <strong>Setting up prescribing</strong>
        </div>
      )}

      {view.kind === 'error' && <ScriptSureErrorNotice view={view.error} patientId={patientId} />}

      {view.kind === 'select' && (
        <ScopePicker
          key={patientId}
          selection={view.selection}
          initialChoice={lastChoice}
          onContinue={handleContinue}
        />
      )}

      {/* Mounted on the session answer, not on the view, so a window open in
          flight or refused leaves what was expanded and any open message as
          they were. Keyed by patient, so a switch drops both. */}
      {showList && <DawPrescriptionList key={patientId} patientId={patientId} />}

      {/* Outside the view branches, so a change in what the tab shows behind
          an open window does not unmount it. Shown only for the patient it was
          opened for: after a switch the reset above closes it, and until that
          runs this keeps the last chart's window off the new one. */}
      <ScriptSureWindow
        opened={openWindow?.patientId === patientId ? openWindow : null}
        onClose={() => setOpenWindow(null)}
        onSignal={() => void queryClient.invalidateQueries({ queryKey: listKey })}
      />
    </div>
  );
}
